import React, { useCallback, useMemo, useRef, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Empty, Btn, Kpi, Chip, Notice, Label } from "../components/ui/index.jsx";
import { useRegister } from "../lib/useRegister.js";
import { useTrackers } from "../lib/useTrackers.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import {
  readSchedule, readWorkbook, readCsv, describeCommit, classify, FORMATS,
} from "../lib/importers/index.js";
import { writeSchedule } from "../lib/importers/writeSchedule.js";
import { writeTracker, mergeTracker } from "../lib/importers/writeTracker.js";
import { matchableColumns } from "../lib/mergeRows.js";
import TrackerChart from "../components/TrackerChart.jsx";
import RegisterImport from "../components/RegisterImport.jsx";
import { suggestView, describeView } from "../lib/trackerViews.js";
import { fmtDate } from "../lib/format.js";
import { pickPhaseLevel } from "../lib/calc.js";

const input = {
  padding: "6px 9px", border: `1px solid ${T.line}`, borderRadius: 4,
  fontSize: 13, background: "#fff", color: T.ink,
};

function DropZone({ onFile, busy }) {
  const [over, setOver] = useState(false);
  const picker = useRef(null);
  const take = (list) => { const f = list?.[0]; if (f) onFile(f); };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
      onClick={() => picker.current?.click()}
      style={{
        border: `2px dashed ${over ? T.gold : T.line}`,
        background: over ? T.gold + "10" : "transparent",
        borderRadius: 6, padding: "34px 20px", textAlign: "center",
        cursor: busy ? "wait" : "pointer",
      }}
    >
      <input ref={picker} type="file" accept=".xlsx,.xlsm,.csv,.xml,.mpp,.xls,.xer"
        style={{ display: "none" }}
        onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
      <div style={{ fontSize: 15, color: T.navy, fontWeight: 700 }}>
        {busy ? "Reading..." : "Drop a file here, or click to choose one"}
      </div>
      <div style={{ fontSize: 13, color: T.soft, marginTop: 6 }}>
        A workbook becomes a tracker. A project plan becomes the schedule.
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- schedules */

function SchedulePreview({ parsed, existing, mode, setMode, summary, onCommit, busy }) {
  const levels = [...new Set(parsed.activities.map((a) => Number(a.outline_level) || 0))]
    .sort((a, b) => a - b);
  /* The same rule the Gantt uses, so the preview and the page agree about
     what a phase is. */
  const counts = new Map(levels.map((l) => [l, parsed.activities.filter(
    (a) => (Number(a.outline_level) || 0) === l).length]));
  const phaseLevel = pickPhaseLevel(counts) ?? levels[0];
  const phases = parsed.activities
    .filter((a) => (Number(a.outline_level) || 0) === phaseLevel)
    .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));

  return (
    <Panel title="What is in this file" accent={T.gold}
      right={<Chip color={T.steel}>{parsed.format}</Chip>}>
      <div style={{ display: "grid", gap: 12, marginBottom: 16,
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
        <Kpi label="Activities" value={parsed.activities.length} />
        <Kpi label="Already finished" value={parsed.finishedCount}
             sub="Keeping their actual dates" color={T.ok} />
        <Kpi label="Milestone tasks" value={parsed.milestoneCount} color={GOLD_INK} />
        <Kpi label="Runs" value={fmtDate(parsed.start)}
             sub={`to ${fmtDate(parsed.finish)}`} color={T.steel} />
      </div>

      {parsed.projectName && (
        <div style={{ marginBottom: 14 }}>
          <Label color={T.navy}>Plan name</Label>
          <div style={{ fontSize: 14, color: T.ink }}>{parsed.projectName}</div>
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr>{["Phase", "Starts", "Finishes", "Complete"].map((h, i) => (
            <th key={h} style={{
              textAlign: i ? "right" : "left", padding: "7px 10px",
              borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
              fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
              whiteSpace: "nowrap",
            }}>{h}</th>
          ))}</tr></thead>
          <tbody>{phases.map((p, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.line}` }}>
              <td style={{ padding: "7px 10px", overflowWrap: "anywhere" }}>{p.title}</td>
              <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtDate(p.start_date)}</td>
              <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                {fmtDate(p.finish_date)}</td>
              <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                {p.percent_complete === null ? "-" : `${p.percent_complete}%`}</td>
            </tr>
          ))}</tbody>
        </table>
        <div style={{ fontSize: 12, color: T.soft, marginTop: 8 }}>
          {phases.length} phases shown. All {parsed.activities.length} activities are
          imported and keep their place in the hierarchy.
        </div>
      </div>

      <div style={{ marginTop: 18, borderTop: `1px solid ${T.line}`, paddingTop: 14 }}>
        <Label color={T.navy}>This contract already has {existing} schedule rows</Label>
        <div style={{ display: "flex", gap: 8, margin: "8px 0 12px", flexWrap: "wrap" }}>
          <Btn small kind={mode === "replace" ? "primary" : "ghost"}
            onClick={() => setMode("replace")}>Replace them</Btn>
          <Btn small kind={mode === "append" ? "primary" : "ghost"}
            onClick={() => setMode("append")}>Add alongside</Btn>
        </div>
        <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 14, lineHeight: 1.6 }}>
          {mode === "replace"
            ? "Replace is the usual choice for a rebaseline. The old rows are retired rather than destroyed."
            : "Add alongside keeps what is there. For a different part of the program rather than a newer version."}
        </div>
        <Btn kind="gold" onClick={onCommit} disabled={busy}>
          {busy ? "Importing..." : `Import ${summary}`}
        </Btn>
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------- workbooks */

function SheetPreview({ sheet }) {
  const spec = useMemo(() => suggestView(sheet.columns, sheet.rows), [sheet]);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {sheet.columns.map((c) => (
          <Chip key={c.key} color={c.type === "number" ? T.steel
            : c.type === "date" ? T.gold : T.soft}>{c.label}</Chip>
        ))}
      </div>
      {spec && (
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, padding: 12 }}>
          <div style={{ fontSize: 12, color: T.soft, marginBottom: 6 }}>
            A starting chart: {describeView(spec, sheet.columns)}. Change it or add
            others once it is in.
          </div>
          <TrackerChart spec={spec} columns={sheet.columns} rows={sheet.rows} height={220} />
        </div>
      )}
    </div>
  );
}

/* Two ways to update a tracker that already has rows, and the difference
 * matters enough to be a choice rather than a default.
 *
 * Replace suits a workbook you redraw every month: the file is the truth and
 * the tracker is a copy of it. Fill in suits an export that only carries what
 * has happened: the plan lists 133 points and the export lists the 33 that are
 * built, and replacing would throw away the hundred nobody has reached yet.
 */
function UpdateMode({ sheet, existing, mode, setMode, fromKey, setFromKey, toKey, setToKey }) {
  const existingColumns = existing.columns ?? [];
  const keys = matchableColumns(existingColumns, sheet.columns, sheet.rows);

  /* If both sides name a column the same way, that is the key, and nobody
     should have to say so. */
  useEffect(() => {
    if (mode !== "merge" || fromKey) return;
    const shared = keys.find((c) => existingColumns.some((e) => e.key === c.key));
    if (shared) { setFromKey(shared.key); setToKey(shared.key); }
    else if (keys.length === 1) setFromKey(keys[0].key);
  }, [mode, keys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const radio = (value, label, detail) => (
    <label style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: "pointer",
                    padding: "8px 10px", borderRadius: 4,
                    background: mode === value ? T.chip : "transparent" }}>
      <input type="radio" name="updatemode" checked={mode === value}
        onChange={() => setMode(value)} style={{ marginTop: 3 }} />
      <span>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.navy }}>{label}</span>
        <span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 2 }}>{detail}</span>
      </span>
    </label>
  );

  return (
    <div style={{ marginTop: 16, border: `1px solid ${T.line}`, borderRadius: 4, padding: 12 }}>
      <Label color={T.navy}>What to do with the {existing.row_count} rows already in {existing.title}</Label>
      <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
        {radio("replace", "Replace every row",
          "The file is the whole truth. Anything typed into the app since the last import goes with it. Saved views are kept.")}
        {radio("merge", "Fill in the rows this file mentions",
          "Match each incoming row to one already there. A match is updated, a new one is added, and everything this file does not mention is left exactly as it is.")}
      </div>

      {mode === "merge" && (
        <div style={{ marginTop: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
          <div style={{ display: "grid", gap: 4 }}>
            <Label color={T.navy}>Identified in this file by</Label>
            <select style={{ ...input, width: 240 }} value={fromKey}
              onChange={(e) => setFromKey(e.target.value)}>
              <option value="">Choose a column</option>
              {keys.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            <Label color={T.navy}>Which matches, in the tracker</Label>
            <select style={{ ...input, width: 240 }} value={toKey}
              onChange={(e) => setToKey(e.target.value)}>
              <option value="">Choose a column</option>
              {existingColumns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {!keys.length && (
            <span style={{ fontSize: 11.5, color: T.risk, maxWidth: 300 }}>
              No column in this file holds a different value on every row, so there
              is nothing to match on. Replace is the only safe choice here.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function WorkbookPreview({ workbook, trackers, onCommit, busy, progress }) {
  const usable = workbook.sheets.filter((s) => s.usable);
  const skipped = workbook.sheets.filter((s) => !s.usable);
  const [selected, setSelected] = useState(usable[0]?.name ?? null);
  const [titles, setTitles] = useState({});
  const [targets, setTargets] = useState({});
  /* A sheet with months across the top is turned round by default, because
     that is what it was drawn to show and charting it as it stands gives one
     bar per measure of height one. */
  const [turned, setTurned] = useState({});
  /* How an update should treat what is already there. Replace is the old
     behaviour and the right one for a workbook you redraw each month. Fill in
     is for an export that only ever carries the rows that changed. */
  const [mode, setMode] = useState("replace");
  const [fromKey, setFromKey] = useState("");
  const [toKey, setToKey] = useState("");

  const raw = usable.find((s) => s.name === selected);
  if (!raw) return null;
  const canTurn = !!raw.wide;
  const turnIt = canTurn && (turned[raw.name] ?? true);
  const sheet = turnIt ? raw.wide : raw;

  /* Filled in rather than ghosted, so the name is something you edited and
     not something the file chose for you while you were not looking. */
  const title = titles[sheet.name] ?? sheet.name;
  const target = targets[sheet.name] ?? "";
  const existing = trackers?.find((t) => t.id === target);

  return (
    <Panel title={workbook.fileName} accent={T.gold}
      right={<Chip color={T.steel}>{usable.length} sheet{usable.length === 1 ? "" : "s"} with a table</Chip>}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {usable.map((s) => (
          <Btn key={s.name} small kind={s.name === selected ? "primary" : "ghost"}
            onClick={() => setSelected(s.name)}>
            {s.name} ({s.rows.length})
          </Btn>
        ))}
      </div>

      {canTurn && (
        <div style={{ marginBottom: 12 }}>
          <Notice>
            This sheet has {raw.wide.periodCount} periods across the top rather than
            down the side.{" "}
            <a onClick={() => setTurned({ ...turned, [raw.name]: !turnIt })}
              style={{ color: T.navy, cursor: "pointer", textDecoration: "underline" }}>
              {turnIt
                ? `Turned round into ${raw.wide.rows.length} rows, one per period. Leave it as it was drawn instead.`
                : `Left as drawn, ${raw.rows.length} rows. Turn it round into one row per period.`}
            </a>
          </Notice>
        </div>
      )}

      <SheetPreview sheet={sheet} />

      {existing && (
        <UpdateMode sheet={sheet} existing={existing} mode={mode} setMode={setMode}
          fromKey={fromKey} setFromKey={setFromKey}
          toKey={toKey} setToKey={setToKey} />
      )}

      <div style={{ marginTop: 18, borderTop: `1px solid ${T.line}`, paddingTop: 14,
                    display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
        <div style={{ display: "grid", gap: 4 }}>
          <Label color={T.navy}>Call this tracker</Label>
          <input style={{ ...input, width: 280 }} value={title} placeholder={sheet.name}
            onChange={(e) => setTitles({ ...titles, [sheet.name]: e.target.value })} />
          <span style={{ fontSize: 11.5, color: T.soft, maxWidth: 280 }}>
            This is what you will look for on the Trackers page. The sheet name is
            filled in. You can rename it later.
          </span>
        </div>

        {(trackers?.length ?? 0) > 0 && (
          <div style={{ display: "grid", gap: 4 }}>
            <Label color={T.navy}>Or update one you already have</Label>
            <select style={{ ...input, width: 280 }} value={target}
              onChange={(e) => setTargets({ ...targets, [sheet.name]: e.target.value })}>
              <option value="">Create a new tracker</option>
              {trackers.map((t) => (
                <option key={t.id} value={t.id}>{t.title} ({t.row_count} rows)</option>
              ))}
            </select>
          </div>
        )}

        <Btn kind="gold" disabled={busy || (existing && mode === "merge" && !(fromKey && toKey))}
          onClick={() => onCommit({
            sheet, title: title || sheet.name, existingId: target || null,
            mode: existing ? mode : "replace", fromKey, toKey,
          })}>
          {busy ? "Importing..."
            : !existing ? `Create it from ${sheet.rows.length} rows`
            : mode === "merge" ? `Fill in ${existing.title} from ${sheet.rows.length} rows`
            : `Replace every row in ${existing.title}`}
        </Btn>
      </div>

      {existing && (
        <div style={{ fontSize: 12.5, color: T.soft, marginTop: 10, lineHeight: 1.6 }}>
          Updating swaps the rows and keeps the tracker's saved views, so next
          month's workbook draws the same charts without setting anything up again.
          The old rows are retired rather than destroyed.
        </div>
      )}

      {progress?.phase === "writing" && (
        <div style={{ marginTop: 12 }}>
          <Notice>Writing {progress.written} of {progress.total} rows...</Notice>
        </div>
      )}

      {skipped.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 12.5, color: T.soft, lineHeight: 1.7 }}>
          <b style={{ color: T.ink }}>Left out:</b>{" "}
          {skipped.map((s) => `${s.name} (${s.reason.toLowerCase().replace(/\.$/, "")})`).join(", ")}.
          Dashboards and notes tabs usually land here, which is right.
        </div>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ page */

export default function Import({ contract, onGoToTrackers }) {
  const { profile } = useAuth();
  const { rows: schedule, reload: reloadSchedule } = useRegister("schedule_stages", contract?.id);
  const { trackers, reload: reloadTrackers } = useTrackers(contract?.id);

  const [kind, setKind] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("replace");
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  /* A workbook can be two different things and the app cannot tell which from
     the file. A register log becomes rows in Action Items or Risks. A tracker
     becomes counts and a chart. So it asks rather than guesses. */
  const [intent, setIntent] = useState("registers");

  const reset = () => { setParsed(null); setWorkbook(null); setKind(null); };

  const onFile = useCallback(async (file) => {
    setError(""); setResult(null); reset(); setBusy(true);
    const what = classify(file);
    setKind(what);
    try {
      if (what === "workbook") setWorkbook(await readWorkbook(file));
      else if (what === "csv") setWorkbook(await readCsv(file));
      else if (what === "mpp" || what === "xls" || what === "unknown") {
        throw new Error(
          what === "mpp"
            ? "This is the binary .mpp format, which a browser cannot open. In MS Project use File, then Save As, then choose XML, and drop that file here instead."
            : what === "xls"
              ? "This is the pre-2007 .xls format. Open it in Excel and save it as .xlsx, then drop that here."
              : `${file.name} is not a file this reads. It handles Excel workbooks, CSV exports and MS Project XML.`,
        );
      } else setParsed(await readSchedule(file));
    } catch (e) {
      setError(e.message); setKind(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const commitSchedule = async () => {
    setBusy(true); setError(""); setProgress(null);
    const r = await writeSchedule({
      contractId: contract.id, activities: parsed.activities,
      /* The logic, which is the difference between a list of dates and a
         schedule. Parsed since the relationships went in and dropped on the
         floor here, because this caller was never told to pass them on. */
      links: parsed.links ?? [],
      mode, userId: profile?.id ?? null, onProgress: setProgress,
    });
    setBusy(false); setProgress(null);
    if (!r.ok) {
      setError(r.partial
        ? `${r.error} ${r.written} activities were written before it stopped. Look at the Schedule page before trying again.`
        : r.error);
      await reloadSchedule();
      return;
    }
    setResult({ kind: "schedule", ...r });
    reset();
    await reloadSchedule();
  };

  const commitWorkbook = async ({ sheet, title, existingId, mode: how, fromKey, toKey }) => {
    setBusy(true); setError(""); setProgress(null);
    const r = existingId && how === "merge"
      ? await mergeTracker({
          contractId: contract.id, trackerId: existingId, sheet,
          fileName: workbook.fileName, fromKey, toKey,
          userId: profile?.id ?? null, onProgress: setProgress,
        })
      : await writeTracker({
          contractId: contract.id, sheet, fileName: workbook.fileName, title,
          existingId, userId: profile?.id ?? null, onProgress: setProgress,
        });
    setBusy(false); setProgress(null);
    if (!r.ok) {
      setError(r.partial
        ? `${r.error} ${r.written} rows were written before it stopped.`
        : r.error);
      await reloadTrackers();
      return;
    }
    setResult({ kind: "tracker", title, ...r });
    reset();
    await reloadTrackers();
  };

  const summary = useMemo(() => (parsed && schedule
    ? describeCommit({ activities: parsed.activities, mode, existing: schedule.length })
    : ""), [parsed, mode, schedule]);

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <Panel title={`Import · ${contract?.name ?? ""}`}>
        <DropZone onFile={onFile} busy={busy} />

        {error && <div style={{ marginTop: 12 }}><Notice kind="error">{error}</Notice></div>}

        {result?.kind === "schedule" && (
          <div style={{ marginTop: 12 }}>
            <Notice kind="ok">
              {result.written} activities imported, {result.linked} linked to a parent
              {result.retired ? `, and ${result.retired} earlier rows retired` : ""}.
              {result.linksWritten
                ? ` ${result.linksWritten} relationships came with them, so the critical path and the float are live.`
                : " No relationships were in this file, so nothing pushes anything downstream yet."}
              {result.kept
                ? ` ${result.kept} activities kept the code they already had, so any baseline still measures against them.`
                : ""}
              {" "}The Schedule page draws this plan now.
            </Notice>
          </div>
        )}

        {result?.kind === "tracker" && (
          <div style={{ marginTop: 12 }}>
            <Notice kind="ok">
              {result.merged
                ? `${result.updated} rows filled in, ${result.added} added, ` +
                  `${result.untouched} left alone. ${result.total} rows in total.`
                : `${result.title}: ${result.written} rows` +
                  (result.replaced ? `, ${result.retired} retired` : "") + "."}{" "}
              {onGoToTrackers && (
                <a onClick={onGoToTrackers} style={{ color: T.navy, cursor: "pointer",
                                                     textDecoration: "underline" }}>
                  Open it and set up its charts
                </a>
              )}
            </Notice>
          </div>
        )}

        {progress?.phase === "reading" && (
          <div style={{ marginTop: 12 }}>
            <Notice>Reading what is already in the tracker...</Notice>
          </div>
        )}
        {(progress?.phase === "updating" || progress?.phase === "adding") && (
          <div style={{ marginTop: 12 }}>
            <Notice>
              {progress.phase === "updating" ? "Filling in" : "Adding"} {progress.written} of {progress.total}...
            </Notice>
          </div>
        )}

        {progress?.phase === "writing" && kind !== "workbook" && (
          <div style={{ marginTop: 12 }}>
            <Notice>Writing {progress.written} of {progress.total}...</Notice>
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 12.5, color: T.soft, lineHeight: 1.7 }}>
          {FORMATS.map((f) => (
            <div key={f.label} style={{ marginBottom: 4 }}>
              <Chip color={f.ready ? T.ok : T.soft}>{f.ready ? "Reads now" : "Not yet"}</Chip>
              {" "}<b style={{ color: T.ink }}>{f.label}</b> ({f.ext}). {f.how}
            </div>
          ))}
        </div>
      </Panel>

      {parsed && (
        <SchedulePreview parsed={parsed} existing={schedule?.length ?? 0}
          mode={mode} setMode={setMode} summary={summary}
          onCommit={commitSchedule} busy={busy} />
      )}

      {workbook && (
        <Panel title="What is in this workbook?">
          <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7, marginBottom: 12 }}>
            The same spreadsheet can be either, and nothing in the file says
            which, so this is your call.
          </div>
          <div style={{ display: "grid", gap: 8,
                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            {[
              ["registers", "A log or a register",
               "Action items, risks, issues, decisions, deliverables, permits. Its rows become rows you can edit, filter and report on."],
              ["tracker", "A tracker",
               "Counts and progress by segment. Its rows become a table with charts on the Trackers page."],
            ].map(([key, title, what]) => (
              <label key={key} style={{
                display: "flex", gap: 9, alignItems: "start", cursor: "pointer",
                padding: "10px 12px", borderRadius: 4,
                border: `1px solid ${intent === key ? T.navy : T.line}`,
                background: intent === key ? T.chip : "transparent",
              }}>
                <input type="radio" name="intent" checked={intent === key}
                  style={{ marginTop: 3 }} onChange={() => setIntent(key)} />
                <span>
                  <b style={{ color: T.navy, fontSize: 13.5 }}>{title}</b>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 3, lineHeight: 1.6 }}>{what}</div>
                </span>
              </label>
            ))}
          </div>
        </Panel>
      )}

      {workbook && intent === "registers" && (
        <RegisterImport contract={contract} workbook={workbook}
          userId={profile?.id ?? null} />
      )}

      {workbook && intent === "tracker" && (
        <WorkbookPreview workbook={workbook} trackers={trackers}
          onCommit={commitWorkbook} busy={busy} progress={progress} />
      )}

      {!parsed && !workbook && (
        <Panel title="What this does">
          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.75 }}>
            <p style={{ marginTop: 0 }}>
              Files dropped here are read in your own browser. Nothing is uploaded
              anywhere and no key or service sees them. Only the rows you confirm
              are written, and only to this contract.
            </p>
            <p>
              <b>A workbook becomes a tracker.</b> Whatever the client has asked
              the program to track, build the spreadsheet the way you always
              would. Its header row becomes the columns and its rows become the
              data, so nothing is defined twice. Then set up its charts once and
              they are there every month, redrawn from next month's workbook
              without touching them.
            </p>
            <p style={{ marginBottom: 0 }}>
              <b>A project plan becomes the schedule.</b> Every activity keeps its
              WBS number, its outline level and its place under its parent, and
              dates the plan records as actual stay marked actual.
            </p>
          </div>
        </Panel>
      )}
    </div>
  );
}
